import { forwardRef, type ButtonHTMLAttributes } from "react";

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  loading?: boolean;
}

/** Shared app button — Fit2Lift brand: Highlight Blue primary, chamfered corners. */
const variantClasses: Record<ButtonVariant, string> = {
  primary:
    "btn-primary text-white focus-visible:ring-2 focus-visible:ring-[var(--color-signal-blue)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-charcoal)] active:scale-[0.98]",
  secondary:
    "bg-transparent text-[var(--color-highlight-blue)] border border-[var(--color-signal-blue)] hover:bg-[var(--color-accent-muted)] hover:border-[var(--color-highlight-blue)] focus-visible:ring-2 focus-visible:ring-[var(--color-signal-blue)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-charcoal)] font-semibold active:scale-[0.98]",
  ghost:
    "bg-transparent text-[var(--color-steel-gray)] border border-[var(--color-border)] hover:bg-[var(--color-surface-elevated)] hover:text-[var(--color-text)] focus-visible:ring-2 focus-visible:ring-[var(--color-border)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-charcoal)] active:scale-[0.98]",
  danger:
    "bg-[var(--color-danger)] text-white hover:bg-[var(--color-danger-hover)] focus-visible:ring-2 focus-visible:ring-[var(--color-danger)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-charcoal)] font-semibold active:scale-[0.98]",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      loading = false,
      disabled,
      className = "",
      children,
      type = "button",
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled ?? loading}
        className={
          "inline-flex items-center justify-center gap-2 min-h-[48px] min-w-[44px] px-4 py-2 btn-chamfer font-[var(--font-display)] text-sm transition-all duration-200 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed outline-none " +
          variantClasses[variant] +
          (className ? " " + className : "")
        }
        style={{
          cursor: disabled || loading ? "not-allowed" : "pointer",
        }}
        {...props}
      >
        {loading ? (
          <>
            <span className="inline-block size-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            <span className="sr-only">Loading</span>
          </>
        ) : (
          children
        )}
      </button>
    );
  }
);

Button.displayName = "Button";
